import { useEffect, useState, useCallback, useRef } from "react";
import {
  fetchUserPreferences,
  patchUserPreferences,
  UserPreferences,
} from "../api/userPreferencesAPI";
import { tokenIdentity } from "../api/authTokenStore";

/**
 * Per-user cache, shared by every instance of the hook.
 *
 * 2026-09-14, Gymnastics of York: a staff session remounted SideNavbarLayout
 * ~1.3 times a SECOND for 90 minutes. Each mount cost two GET
 * /users/me/preferences (the layout calls this hook directly and again through
 * useWhatsNew), so one stuck session produced 12,080 preference fetches and
 * ~6 req/s against the tenant database — measured, and it made the app
 * unusable for everyone at that club.
 *
 * The remount trigger is a separate bug. This makes a remount FREE: the answer
 * is the same for the same person, so it is fetched once and reused, and
 * concurrent mounts share one in-flight request instead of racing. Keyed on
 * who the token is for, not the token string — the string changes on every
 * session slide while the person does not.
 */
let cached: { identity: string; data: UserPreferences } | null = null;
let inflight: { identity: string; promise: Promise<UserPreferences | null> } | null = null;

/** Test seam: forget everything cached. */
export function _resetUserPreferencesCache(): void {
  cached = null;
  inflight = null;
}

/**
 * Per-user UI preferences hook. Fetches once on mount, exposes the prefs
 * map and a setter that does optimistic-local-update + debounced backend
 * save. Keys are free-form; the caller decides what to store.
 *
 *   const { prefs, setPref, ready } = useUserPreferences();
 *   const perPage = Number(prefs.memberships_per_page) || 20;
 *   ...
 *   <select onChange={(e) => setPref("memberships_per_page", Number(e.target.value))}>
 */
export function useUserPreferences(): {
  prefs: UserPreferences;
  setPref: (key: string, value: unknown) => void;
  ready: boolean;
} {
  const token = typeof window !== "undefined" ? localStorage.getItem("authToken") || "" : "";
  const identity = tokenIdentity(token);
  const hit = identity && cached?.identity === identity ? cached : null;

  const [prefs, setPrefs] = useState<UserPreferences>(hit ? hit.data : {});
  const [ready, setReady] = useState(!!hit);
  const pendingRef = useRef<UserPreferences>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setReady(true);
      return;
    }
    // Already answered for this person — a remount costs nothing.
    if (identity && cached?.identity === identity) {
      setPrefs(cached.data);
      setReady(true);
      return;
    }

    // Share one request across every instance mounting at the same moment.
    // A token we cannot read an identity from still fetches, exactly as before
    // — it simply can't be cached or shared. Skipping the fetch there would
    // silently leave the caller with no preferences at all.
    let promise: Promise<UserPreferences | null>;
    if (identity) {
      if (!inflight || inflight.identity !== identity) {
        inflight = { identity, promise: fetchUserPreferences(token) };
      }
      promise = inflight.promise;
    } else {
      promise = fetchUserPreferences(token);
    }

    // Only ours to clear. If another identity replaced `inflight` while this
    // request was in the air, leave theirs alone.
    const mine = identity ? inflight : null;
    promise.then((data) => {
      if (mine && inflight === mine) inflight = null;
      // A failed fetch resolves null; don't cache that as "this user has none".
      if (identity && data && typeof data === "object") cached = { identity, data };
      if (cancelled) return;
      if (data && typeof data === "object") setPrefs(data);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [token, identity]);

  const flush = useCallback(() => {
    const partial = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(partial).length === 0) return;
    if (!token) return;
    patchUserPreferences(token, partial);
  }, [token]);

  // Persist any pending writes if the user closes the tab mid-debounce.
  useEffect(() => {
    const handler = () => flush();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [flush]);

  const setPref = useCallback(
    (key: string, value: unknown) => {
      setPrefs((prev) => {
        // Skip the work if the value didn't actually change.
        if (prev[key] === value) return prev;
        return { ...prev, [key]: value };
      });
      // Keep the shared cache current, or a remount would read back the old
      // value and every other mounted instance would stay stale.
      if (identity) {
        const base = cached?.identity === identity ? cached.data : {};
        if (base[key] !== value) cached = { identity, data: { ...base, [key]: value } };
      }
      pendingRef.current = { ...pendingRef.current, [key]: value };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, 600);
    },
    [flush, identity]
  );

  return { prefs, setPref, ready };
}

/**
 * A filter/control state that persists per-user across sessions.
 *
 * Wraps a useState pair: the local state stays the source of truth for
 * the control (synchronous re-render), the saved preference is adopted
 * once when prefs load, and every set writes back through setPref
 * (debounced in useUserPreferences). Saved values are validated before
 * adoption so a stale/corrupt pref can never wedge a page.
 *
 *   const userPrefs = useUserPreferences();
 *   const [statusFilter, setStatusFilter, statusAdopted] = usePersistedFilter(
 *     userPrefs, "memberships_status_filter", "all",
 *     (v): v is string => typeof v === "string" && ["active","inactive","archived","all"].includes(v)
 *   );
 *
 * The third element flips true on the render after the saved value has
 * been applied — pages that fetch imperatively (Apply-button style) can
 * key their initial fetch on it so the first result set reflects the
 * saved filters.
 */
export function usePersistedFilter<T>(
  userPrefs: {
    prefs: UserPreferences;
    setPref: (key: string, value: unknown) => void;
    ready: boolean;
  },
  key: string,
  defaultValue: T,
  isValid: (value: unknown) => boolean
): [T, (value: T) => void, boolean] {
  const [value, setValue] = useState<T>(defaultValue);
  const [adopted, setAdopted] = useState(false);
  const { prefs, setPref, ready } = userPrefs;

  useEffect(() => {
    if (!ready || adopted) return;
    const saved = prefs[key];
    if (saved !== undefined && isValid(saved)) {
      setValue(saved as T);
    }
    setAdopted(true);
    // Adopt exactly once when prefs become ready — after that the
    // control drives the value (same pattern as the per-page prefs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const set = useCallback(
    (next: T) => {
      setValue(next);
      setPref(key, next);
    },
    [setPref, key]
  );

  return [value, set, adopted];
}
